/**
 * Die Testbrett-Ansicht — **der Gegenstand, von dem die Designabstimmung ausgeht.**
 *
 * `initPrompt.md` (Ergänzungsblock) gibt die Reihenfolge vor: Geometrie, Rule Engine,
 * **funktionales, bewusst neutrales Test-Board**, Spiellogik darauf testen — und erst danach ein
 * konkreter visueller Entwurf, abgestimmt mit dem User. Dieser Bildschirm ist Schritt 3: ein
 * Brett, das man anschauen und anfassen kann.
 *
 * **Ohne Partiedaten und ohne Berechtigung.** Er zeigt die 217 Felder mit ihren kanonischen
 * Namen, nichts weiter. Deshalb braucht er keinen Token — und darf auch keinen bekommen, sonst
 * wäre er eine zweite Stelle, an der Partiedaten entstehen könnten.
 *
 * **Die Feldnamen sind der Zweck.** `green-A1` ist die Adresse, über die sich Regelfragen,
 * Fehlerberichte und Designentscheidungen überhaupt besprechen lassen — genau diese Namen stehen
 * in der Referenzgrafik, in der Engine und in jedem Protokoll.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cellId, createBoard, createSectorLayout, type Hex } from '@flaghex/engine';

import { BoardSvg } from '../board/BoardSvg.js';

/**
 * Die Vorgaben, mit denen das Testbrett erscheint.
 *
 * Dieselben Werte wie der Default der RuleConfig (Kantenlänge 9) und die entschiedene Startzone
 * (zwei Reihen, Nr. 11). **Nicht** aus der RuleConfig gelesen: diese Ansicht hat keine Partie,
 * und die globale Konfiguration gehört hinter den Admin-Schlüssel (Zyklus 10d).
 */
const SIDE_LENGTH = 9;
const START_ZONE_ROWS = 2;

export function BoardScreen() {
  const { t } = useTranslation(['board', 'common']);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [selected, setSelected] = useState<Hex | undefined>(undefined);

  // Das Layout nur für die Anzeige des gewählten Feldnamens — das Brett rechnet sein eigenes.
  const layout = createSectorLayout(createBoard({ sideLength: SIDE_LENGTH }));

  return (
    <main>
      <h1>{t('board:name')}</h1>
      <p>
        <label>
          <input
            type="checkbox"
            checked={showCoordinates}
            onChange={(event) => setShowCoordinates(event.target.checked)}
          />{' '}
          {t('board:showCoordinates')}
        </label>
      </p>
      <p role="status">
        {selected === undefined
          ? t('board:nothingSelected')
          : t('board:selected', { id: cellId(layout, selected) })}
      </p>
      <BoardSvg
        sideLength={SIDE_LENGTH}
        startZoneRows={START_ZONE_ROWS}
        showCoordinates={showCoordinates}
        selected={selected}
        onSelect={setSelected}
      />
    </main>
  );
}
